import { useState, useEffect } from "react";

// ─────────────────────────────────────────
// Página de carga masiva del Backlog OM Fijo — SOLO Administrador.
// Mismo patrón visual/funcional que CargaDispo, pero para /cargar-backlog.
// Se monta en App.js dentro del bloque `{esAdmin && ...}`, igual que
// /gestion-huella, /cargar y /usuarios.
// ─────────────────────────────────────────
const CLARO_RED   = "#E30613";
const CLARO_DARK  = "#0f172a";
const CLARO_MUTED = "#64748b";
const BORDER      = "#e2e8f0";

const card = {
  background: "white", borderRadius: "14px", border: `1px solid ${BORDER}`,
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)", padding: "24px",
};

function CargarBacklog({ usuario }) {
  const API = process.env.REACT_APP_API_URL;
  const esAdmin = (usuario?.rol || "").toLowerCase().trim() === "administrador";

  const [archivo, setArchivo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [cortes, setCortes] = useState([]);

  useEffect(() => {
    fetch(`${API}/backlog-cortes-disponibles`).then((r) => r.json()).then((data) => setCortes(data || [])).catch(() => {});
  }, [API]);

  const subir = () => {
    if (!archivo) return;
    setCargando(true); setResultado(null);
    const form = new FormData();
    form.append("archivo", archivo);
    form.append("rol", usuario?.rol || "");
    form.append("cedula", usuario?.cedula || "");
    fetch(`${API}/cargar-backlog`, { method: "POST", body: form })
      .then((r) => r.json())
      .then((data) => {
        setResultado(data);
        if (data.ok) {
          setArchivo(null);
          fetch(`${API}/backlog-cortes-disponibles`).then((r) => r.json()).then((c) => setCortes(c || []));
        }
      })
      .catch(() => setResultado({ ok: false, mensaje: "No se pudo conectar con el servidor." }))
      .finally(() => setCargando(false));
  };

  if (!esAdmin) {
    return (
      <div style={{ ...card, maxWidth: "520px", margin: "40px auto", textAlign: "center" }}>
        <div style={{ fontSize: "36px", marginBottom: "8px" }}>⛔</div>
        <h3 style={{ margin: "0 0 8px 0", color: CLARO_DARK }}>Acceso restringido</h3>
        <p style={{ margin: 0, color: CLARO_MUTED, fontSize: "13.5px" }}>Solo el Administrador puede cargar el backlog.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: "18px" }}>
        <h2 style={{ margin: 0, fontSize: "20px", color: CLARO_DARK, fontWeight: "800" }}>Cargar Backlog OM Fijo R3</h2>
        <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: CLARO_MUTED }}>Sube el archivo diario con las 101 columnas base (igual a la hoja "Backlog Avanza" del Excel de referencia).</p>
      </div>

      <div style={card}>
        <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: CLARO_MUTED, lineHeight: 1.6 }}>
          Acepta <strong>.xlsx</strong>, <strong>.xls</strong> y <strong>.csv</strong> (cualquier separador: coma, punto y coma o tabulador — se detecta solo).
          El orden de las columnas no importa: se reconocen por nombre, así que si el archivo viene desordenado, con columnas de más o de menos, igual se organiza y carga.
          Si subes un Excel con varias pestañas, se detecta automáticamente cuál es la del backlog. Las filas duplicadas (misma OT + misma fecha de corte) se ignoran,
          así que puedes volver a subir el mismo archivo sin riesgo de duplicar información. El rango de antigüedad (columna "Rangos") se recalcula solo después de cada carga.
        </p>

        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <input type="file" accept=".xlsx,.xls,.xlsm,.csv" onChange={(e) => setArchivo(e.target.files?.[0] || null)} style={{ fontSize: "13px" }} />
          <button onClick={subir} disabled={!archivo || cargando} style={{
            padding: "10px 20px", borderRadius: "10px", border: "none", cursor: archivo ? "pointer" : "not-allowed",
            background: cargando ? "#9ca3af" : `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`,
            color: "white", fontSize: "13.5px", fontWeight: "700",
          }}>{cargando ? "Cargando…" : "⬆️ Subir archivo"}</button>
        </div>

        {resultado && (
          <div style={{
            marginTop: "16px", padding: "12px 16px", borderRadius: "10px", fontSize: "13.5px",
            background: resultado.ok ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${resultado.ok ? "#86efac" : "#fca5a5"}`,
            color: resultado.ok ? "#15803d" : "#991b1b",
          }}>
            {resultado.ok
              ? `✅ ${resultado.insertadas} filas insertadas, ${resultado.duplicadas} duplicadas (de ${resultado.total_archivo} en el archivo).`
              : `❌ ${resultado.mensaje || "Error al cargar el archivo."}`}
          </div>
        )}
        {resultado?.ok && typeof resultado.columnas_reconocidas === "number" && (
          <div style={{
            marginTop: "8px", fontSize: "12px",
            color: resultado.columnas_reconocidas < resultado.columnas_esperadas ? "#b45309" : CLARO_MUTED,
          }}>
            {resultado.columnas_reconocidas < resultado.columnas_esperadas
              ? `⚠️ Se reconocieron ${resultado.columnas_reconocidas} de ${resultado.columnas_esperadas} columnas esperadas — revisa que los nombres de encabezado coincidan con los de la hoja "Backlog Avanza" de referencia.`
              : `Se reconocieron las ${resultado.columnas_esperadas} columnas esperadas.`}
          </div>
        )}
      </div>

      {cortes.length > 0 && (
        <div style={{ ...card, marginTop: "16px" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "14px", color: CLARO_DARK }}>Cortes ya cargados</h3>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {cortes.map((c) => (
              <span key={c} style={{
                padding: "5px 12px", borderRadius: "20px", background: "#f1f5f9",
                fontSize: "12px", fontWeight: "600", color: CLARO_DARK,
              }}>{c}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CargarBacklog;
